import { Box } from '@mui/material';
import { useMemo } from 'react';

import {
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { SupplierBadge, SupplierBadgeItem } from 'shared/ui/SupplierBadge';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { SourcingTooltip } from './SourcingTooltip';

interface SuppliersForItemProps {
    selectedProposals: SelectedProposal[];
    showEmptyProposals?: boolean;
    opacity?: number;
    proposals?: Proposal[];
    subtitle?: string;
    onViewMoreSuppliers?: () => void;
}

const getSelectedQuantity = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return 0;
    }

    return (proposal as SelectedProposal).selectedQuantity ?? 0;
};

const getSellerUid = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return (proposal as Proposal).seller.uid;
    }

    return (proposal as SelectedProposal).sellerUid;
};

const getName = (
    proposal: Proposal | SelectedProposal,
    showEmptyProposals: boolean,
) => {
    if (showEmptyProposals) {
        return (proposal as Proposal).seller.name;
    }

    return (proposal as SelectedProposal).name;
};

export const SuppliersForItem = ({
    selectedProposals,
    showEmptyProposals = false,
    opacity = 1,
    proposals,
    subtitle,
    onViewMoreSuppliers,
}: SuppliersForItemProps) => {
    const validProposals = useMemo(() => {
        const proposalSource: (Proposal | SelectedProposal)[] =
            showEmptyProposals
                ? proposals?.slice(0, 1) || []
                : selectedProposals;

        return proposalSource
            .filter(
                (proposal: Proposal | SelectedProposal) =>
                    getSelectedQuantity(proposal, showEmptyProposals) > 0 ||
                    showEmptyProposals,
            )
            .sort(
                (
                    a: Proposal | SelectedProposal,
                    b: Proposal | SelectedProposal,
                ) =>
                    getSelectedQuantity(a, showEmptyProposals) -
                    getSelectedQuantity(b, showEmptyProposals),
            );
    }, [proposals, selectedProposals, showEmptyProposals]);

    const badgeItems = useMemo<SupplierBadgeItem[]>(
        () =>
            validProposals
                .map((p) => {
                    const sellerUid = getSellerUid(p, showEmptyProposals);
                    if (!sellerUid) return null;

                    return {
                        uid: p.uid,
                        sellerUid,
                        name: getName(p, showEmptyProposals),
                    };
                })
                .filter((item): item is SupplierBadgeItem => item !== null),
        [validProposals, showEmptyProposals],
    );

    if (badgeItems.length === 0) {
        return null;
    }

    const hasActiveSelections = selectedProposals?.some(
        (p) => (p.selectedQuantity ?? 0) > 0,
    );
    const showTooltip = !showEmptyProposals && hasActiveSelections;

    const badge = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '18px',
                justifyContent: 'center',
                gap: '6px',
            }}
        >
            <SupplierBadge
                proposals={badgeItems}
                opacity={opacity}
                {...(showEmptyProposals && { maxLogos: 1, maxNames: 1 })}
            />
            {subtitle && (
                <Box
                    sx={{
                        color: '#F00',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '14px',
                    }}
                >
                    {subtitle}
                </Box>
            )}
        </Box>
    );

    if (!showTooltip) {
        return badge;
    }

    return (
        <DynamicTooltip
            trigger="hover"
            interactive
            placement="bottom-start"
            offset={[0, 8]}
            content={
                <SourcingTooltip
                    selectedProposals={selectedProposals}
                    proposals={proposals ?? []}
                    onViewMoreSuppliers={onViewMoreSuppliers}
                />
            }
        >
            {badge}
        </DynamicTooltip>
    );
};
